import { useId, useState } from "react";
import { useLanguage } from "../LanguageContext";
import { padIndex } from "../lib/format";
import { IconChevron } from "./Icons";

type TimelineExperienceListProps = {
	experiences: Experience[];
};

function TimelineExperienceItem({
	experience,
	index,
}: {
	experience: Experience;
	index: number;
}) {
	const { text } = useLanguage();
	const panelId = useId();
	const [open, setOpen] = useState(false);
	const hasDetails = Boolean(
		(experience.details && experience.details.length > 0) ||
		experience.technologies,
	);
	const isLife = experience.type === "personal";
	const heading = experience.title || experience.location;

	return (
		<article className={`timeline-item${open ? " is-open" : ""}`}>
			<div className="timeline-item-summary">
				<span className="timeline-item-index">{padIndex(index + 1)}</span>
				<div className="timeline-item-copy">
					<strong>
						{isLife ? (
							experience.title && experience.location ? (
								<>
									{experience.title}
									{" · "}
									{experience.locationURL ? (
										<a
											href={experience.locationURL}
											target="_blank"
											rel="noreferrer"
										>
											{experience.location}
										</a>
									) : (
										experience.location
									)}
								</>
							) : experience.locationURL ? (
								<a
									href={experience.locationURL}
									target="_blank"
									rel="noreferrer"
								>
									{heading}
								</a>
							) : (
								heading
							)
						) : experience.titleURL ? (
							<a href={experience.titleURL} target="_blank" rel="noreferrer">
								{heading}
							</a>
						) : (
							heading
						)}
					</strong>
					{!isLife ? (
						<p>
							{experience.locationURL ? (
								<a
									href={experience.locationURL}
									target="_blank"
									rel="noreferrer"
								>
									{experience.location}
								</a>
							) : (
								experience.location
							)}
							{experience.description ? ` · ${experience.description}` : ""}
						</p>
					) : null}
				</div>
				<span
					className="timeline-item-type"
					aria-label={`${text.timeline.typeLabel}: ${text.timeline.experienceKind[experience.type]}`}
				>
					{text.timeline.experienceKind[experience.type]}
				</span>
				<span
					className="timeline-item-status"
					aria-label={`${text.timeline.statusLabel}: ${experience.time}`}
				>
					{experience.time}
				</span>
				<div className="timeline-item-action">
					{hasDetails ? (
						<button
							type="button"
							className="timeline-item-trigger"
							aria-expanded={open}
							aria-controls={panelId}
							aria-label={
								open ? text.timeline.detailsHide : text.timeline.detailsShow
							}
							onClick={() => setOpen((value) => !value)}
						>
							<IconChevron width="16" height="16" />
						</button>
					) : null}
				</div>
			</div>
			{hasDetails ? (
				<div
					id={panelId}
					className="timeline-item-panel"
					role="region"
					aria-hidden={!open}
				>
					<div className="timeline-item-panel-inner">
						{experience.details && experience.details.length > 1 ? (
							<ul className="timeline-item-details is-bullets">
								{experience.details.map((detail) => (
									<li key={detail}>{detail}</li>
								))}
							</ul>
						) : experience.details?.[0] ? (
							<p className="timeline-item-details">{experience.details[0]}</p>
						) : null}
						{experience.technologies ? (
							<p className="timeline-item-stack">
								<span>{text.timeline.stackLabel}</span>
								{experience.technologies}
							</p>
						) : null}
					</div>
				</div>
			) : null}
		</article>
	);
}

export default function TimelineExperienceList({
	experiences,
}: TimelineExperienceListProps) {
	const total = experiences.length;

	return (
		<div className="home-index timeline-index">
			<div className="timeline-index-head" aria-hidden="true">
				<span className="timeline-item-index" />
				<span className="timeline-item-copy" />
				<span className="timeline-item-type" />
				<span className="timeline-item-status" />
				<span className="timeline-item-action" />
			</div>
			{[...experiences].reverse().map((experience, index) => (
				<TimelineExperienceItem
					key={`${experience.time}-${experience.title || experience.location}-${index}`}
					experience={experience}
					index={total - index - 1}
				/>
			))}
		</div>
	);
}
